/**
 * Device-side state for PIN sign-in.
 *
 * - deviceId: random id for this browser install (sent with password logins).
 * - trusted accounts: people who ticked "Trust this device" here — up to 5
 *   tiles on the login screen, each with the device secret the server issued
 *   (rotated on every PIN login).
 * - last email: prefilled on the password form (only when the device was trusted).
 *
 * Browser storage can be blocked (private mode) — every access is guarded and
 * the login screen falls back to the plain password form.
 */
const DEVICE_ID_KEY = 'pmms.deviceId'
const ACCOUNTS_KEY = 'pmms.trustedAccounts'
const LAST_EMAIL_KEY = 'pmms.lastEmail'
export const MAX_ACCOUNTS_PER_DEVICE = 5

export interface TrustedAccount {
  userId: string
  firstName: string
  lastName: string
  email: string
  avatarUrl?: string | null
  token: string
  /** "Remember me" from the password login — PIN sessions last as long. */
  remember: boolean
  lastUsedAt: number
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage blocked — PIN sign-in simply isn't offered on this device
  }
}

export function getDeviceId(): string {
  let id = read<string | null>(DEVICE_ID_KEY, null)
  if (!id) {
    id = crypto.randomUUID()
    write(DEVICE_ID_KEY, id)
  }
  return id
}

/** Most recently used first. */
export function getTrustedAccounts(): TrustedAccount[] {
  const list = read<TrustedAccount[]>(ACCOUNTS_KEY, [])
  return Array.isArray(list) ? [...list].sort((a, b) => b.lastUsedAt - a.lastUsedAt) : []
}

export function saveTrustedAccount(account: Omit<TrustedAccount, 'lastUsedAt'>) {
  const others = getTrustedAccounts().filter((a) => a.userId !== account.userId)
  const next = [{ ...account, lastUsedAt: Date.now() }, ...others].slice(0, MAX_ACCOUNTS_PER_DEVICE)
  write(ACCOUNTS_KEY, next)
}

export function removeTrustedAccount(userId: string) {
  write(
    ACCOUNTS_KEY,
    getTrustedAccounts().filter((a) => a.userId !== userId),
  )
}

export function findTrustedAccount(userId: string): TrustedAccount | undefined {
  return getTrustedAccounts().find((a) => a.userId === userId)
}

export function getLastEmail(): string {
  return read<string>(LAST_EMAIL_KEY, '')
}

export function setLastEmail(email: string | null) {
  write(LAST_EMAIL_KEY, email)
}
